import { get, map, size } from "lodash";
import React from "react";
import styled from "styled-components";

import { arrayWithNEmptyItems } from "../../../utils/array";
import Input from "./ReadOnlyInput";
import { Label, Text } from "./styles";

const columnsExample = [
  {
    id: "atributeValue",
    name: "Nome Legível",
    render: (value, id, entry) => "",
    custom: false,
    style: {
      width: 20,
      justify: "flex-start", // flex rules
      align: "center", //flex rules,
      // styles contained in ReadOnlyInput
    },
  },
];

// static
const renderHeaderCell = (styles) => (column) => {
  const { name } = column;
  const { spacing } = styles;
  return (
    <HeaderCell>
      <HeaderCellContentWrapper spacing={spacing}>
        <Label marginBottom={0}>{name}</Label>
      </HeaderCellContentWrapper>
    </HeaderCell>
  );
};

// static
const renderHeader = (columns, loading, styles) => {
  return (
    <HeaderRow hide={loading}>
      {map(columns, renderHeaderCell(styles))}
    </HeaderRow>
  );
};

// static
const renderCell = (entry, loading, styles, selected) => (column) => {
  const { id, custom, render, style = {} } = column;
  const { width, ...otherStyles } = style;
  const { spacing, rowHeight, selectedRowColor } = styles;

  let cellWidth;
  let inputWidth;
  if (typeof width === "number") {
    inputWidth = width + "px";
    cellWidth = inputWidth;
  } else {
    cellWidth = width;
  }

  const value = get(entry, id, "");
  const color = selected ? selectedRowColor : undefined;
  return (
    <Cell width={cellWidth}>
      <CellContentWrapper spacing={spacing}>
        {custom && render && render(value, id, entry)}
        {!custom && (
          <Input
            {...otherStyles}
            color={color}
            height={rowHeight}
            width={inputWidth}
            loading={loading}
            value={render ? render(value, id, entry) : value}
          />
        )}
      </CellContentWrapper>
    </Cell>
  );
};

// static
const renderRow =
  (columns, loading, styles, onRowClick, selectedRow) => (entry, index) => {
    if (!columns) return null;

    const handleClick = () => {
      if (onRowClick) {
        onRowClick(entry);
      }
    };

    const selected = selectedRow && selectedRow === entry;

    return (
      <Row key={index} clickable={!!onRowClick} onClick={handleClick}>
        {map(columns, renderCell(entry, loading, styles, selected))}
      </Row>
    );
  };

const TableComponent = (props) => {
  const {
    maxHeight = "auto",
    spacing = 1,
    selectedRowColor = "#d6d6d6",
    rowHeight = 32,
    noDataMessage = "Sem informações",
    onRowClick,
    loadingRows = 5,
    loading = false,
    entries = [],
    columns = [],
    total = {},
    totalColumns = [],
    selectedRow,
  } = props;

  const styles = {
    spacing,
    rowHeight,
    selectedRowColor,
  };

  if (!loading && size(entries) == 0) {
    return <Text>{noDataMessage}</Text>;
  }

  const rows = loading ? arrayWithNEmptyItems(loadingRows) : entries;
  return (
    <TableWrapper maxHeight={maxHeight}>
      <Table>
        {renderHeader(columns, loading, styles)}
        {map(
          rows,
          renderRow(columns, loading, styles, onRowClick, selectedRow)
        )}
        {renderRow(totalColumns, loading, styles)({ id: total }, 0)}
      </Table>
    </TableWrapper>
  );
};

const TableWrapper = styled.div`
  width: 100%;
  max-height: ${({ maxHeight }) => maxHeight};
  overflow-y: auto;
  overflow-x: auto;

  /* Scrollbar: Wrapper */
  ::-webkit-scrollbar {
    height: 16px;
  }

  /* Scrollbar: Background */
  ::-webkit-scrollbar-track {
    background: #fff;
  }

  /* Scrollbar: Scroll */
  ::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 8px;
    border: solid 3px #fff;
  }

  /* Scrollbar: Scroll on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #aaa;
  }
`;

const Table = styled.table`
  width: 100%;

  tr,
  td {
    flex-grow: 1;
  }
  tr:nth-child(2) > td > div {
    padding-top: 0;
  }
  td:first-child > div {
    padding-left: 0;
  }
`;

const HeaderCell = styled.td`
  padding-left: ${({ spacing = 0 }) => spacing * 8}px;
`;

const HeaderCellContentWrapper = styled.div`
  padding-left: ${({ spacing = 0 }) => spacing * 8}px;
`;

const HeaderRow = styled.tr`
  opacity: ${({ hide }) => (hide ? 0 : 1)};
`;

const Row = styled.tr`
  cursor: ${({ clickable }) => (clickable ? "pointer" : "auto")};
`;

const Cell = styled.td`
  width: ${({ width = "auto" }) => width};
`;

const CellContentWrapper = styled.div`
  padding-top: ${({ spacing = 0 }) => spacing * 8}px;
  padding-left: ${({ spacing = 0 }) => spacing * 8}px;
`;

export default TableComponent;
